import { Box, Button, ButtonGroup, Checkbox, Textarea } from '@chakra-ui/react';
import { useState } from 'react';
import { ApplicantStatusReason, IApplyUniConfig } from 'types';
import { CustomFormControl, Modal, Select } from 'shared-components';
import { statusReasonOptions } from 'data';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';

interface WithdrawRejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  isWithdraw: boolean; // true for withdraw, false for reject
  onConfirm: (
    statusReason: ApplicantStatusReason,
    statusExplanationToApplicant: string,
    statusNote: string,
    notifyApplicant: boolean,
  ) => void;
  isLoading: boolean;
}

export const WithdrawRejectModal = ({
  isOpen,
  onClose,
  isWithdraw,
  onConfirm,
  isLoading,
}: WithdrawRejectModalProps) => {
  const [statusReasonType, setStatusReasonType] = useState<ApplicantStatusReason | ''>('');
  const [statusNote, setStatusNote] = useState('');
  const [statusExplanationToApplicant, setExplanationToApplicant] = useState('');
  const [notifyApplicant, setNotifyApplicant] = useState(false);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);

  const getDefaultExplanationToApplicant = (reason: ApplicantStatusReason | ''): string => {
    if (!reason) {
      return '';
    }

    const reasonType = (reason.charAt(0).toLowerCase() + reason.slice(1)) as keyof NonNullable<
      IApplyUniConfig['rejectionReasons']
    >;

    return applyConfig.rejectionReasons?.[reasonType] ?? '';
  };

  const handleConfirm = () => {
    if (statusReasonType && String(statusReasonType) !== '') {
      onConfirm(statusReasonType as ApplicantStatusReason, statusExplanationToApplicant, statusNote, notifyApplicant);
    }
  };

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title={isWithdraw ? 'Withdraw offer' : 'Reject'}
      footer={
        <ButtonGroup spacing={3} size={'lg'}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="solid"
            colorScheme="red"
            isDisabled={!statusReasonType || String(statusReasonType) === ''}
            onClick={handleConfirm}
            isLoading={isLoading}
            data-selector="reject-confirm-button"
          >
            {isWithdraw ? 'Withdraw offer' : 'Reject programme'}
          </Button>
        </ButtonGroup>
      }
    >
      <Box>
        <Select
          id={'status-reason-type'}
          key={'status-reason-type'}
          options={statusReasonOptions}
          value={statusReasonType}
          placeHolder="Choose reason"
          label="Reason"
          onChange={(evt: React.FormEvent<HTMLSelectElement>) => {
            const reason = evt.currentTarget.value as ApplicantStatusReason;
            setStatusReasonType(reason);
            setExplanationToApplicant(getDefaultExplanationToApplicant(reason));
          }}
          mb={8}
          size="lg"
        />

        <CustomFormControl
          id="status-explanation-to-applicant"
          label="Explanation to applicant"
          labelProps={{ htmlFor: 'status-explanation-to-applicant' }}
          size="lg"
        >
          <Textarea
            mb={8}
            placeholder=""
            value={statusExplanationToApplicant}
            onChange={e => setExplanationToApplicant(e.target.value)}
            data-selector="status-explanation-to-applicant-textarea"
          />
        </CustomFormControl>

        <CustomFormControl
          id="status-note"
          label="Internal note"
          labelProps={{ htmlFor: 'status-note' }}
          subLabel="This will never be shown to the applicant"
          size="lg"
        >
          <Textarea mb={8} placeholder="" value={statusNote} onChange={e => setStatusNote(e.target.value)} />
        </CustomFormControl>

        <Checkbox isChecked={notifyApplicant} onChange={e => setNotifyApplicant(e.target.checked)} size="lg">
          Notify applicant by email
        </Checkbox>
      </Box>
    </Modal>
  );
};
